import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils/ui";
import {
  DistanceUnit,
  distanceDisplayToMeters,
  getDistanceDisplay,
  metersToDistanceDisplay,
} from "@/lib/utils/exerciseTracking";
import { useOrg } from "@/lib/states/session/hooks";
import { Exercise, ExerciseSet, TrackingType } from "@/components/workouts/workoutsColumns";
import { Plus, X } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

type PeriodisationExerciseSetCardProps = {
  exercise: Exercise;
  onSetsChange: (sets: ExerciseSet[]) => void;
};

const formatTimeInput = (seconds?: number | null) => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
    return "";
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const parseTimeDigitsToSeconds = (value: string) => {
  const digits = value.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  if (!digits) return null;
  const minutesPart = digits.length > 2 ? digits.slice(0, -2) : "";
  const secondsPart = digits.slice(-2);
  const minutes = minutesPart ? Number(minutesPart) : 0;
  const seconds = Number(secondsPart);
  if (Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
  return minutes * 60 + seconds;
};

export const PeriodisationExerciseSetCard = ({
  exercise,
  onSetsChange,
}: PeriodisationExerciseSetCardProps) => {
  const org = useOrg();
  const distanceUnit: DistanceUnit =
    org?.distanceUnitDefault === "mi" ? "mi" : "km";
  const trackingType = (exercise.tracking_type ?? "strength") as TrackingType;
  const isDistance = trackingType.startsWith("distance");
  const isTimed = trackingType === "timed";
  const distanceDisplay = getDistanceDisplay(trackingType, distanceUnit);
  const sets = exercise.sets ?? [];

  const updateSet = (id: string, updates: Partial<ExerciseSet>) => {
    onSetsChange(sets.map((set) => (set.id === id ? { ...set, ...updates } : set)));
  };

  const removeSet = (id: string) => {
    if (sets.length <= 1) return;
    onSetsChange(sets.filter((set) => set.id !== id));
  };

  const addSet = () => {
    let nextSet: ExerciseSet;
    if (sets.length > 0) {
      nextSet = { ...sets[sets.length - 1], id: uuidv4() };
    } else if (isTimed || isDistance) {
      nextSet = { id: uuidv4() };
    } else {
      nextSet = { id: uuidv4(), startRange: 10, endRange: 12 };
      if (trackingType === "strength") {
        nextSet.targetWeight = 0;
      }
    }
    onSetsChange([...sets, nextSet]);
  };

  return (
    <div className="w-full rounded-md bg-background p-3">
      <div className="mb-1 flex flex-wrap gap-2 px-1.5 py-1 text-[11px] text-muted-foreground lg:flex-nowrap">
        <span className="w-7 text-center">SET</span>
        <div className="flex flex-grow items-center justify-center gap-2">
          {!isTimed && !isDistance && (
            <>
              <span className="w-[104px] text-center">REP RANGE</span>
              {trackingType === "strength" && (
                <span className="w-24 whitespace-nowrap text-center">
                  TARGET WEIGHT
                </span>
              )}
            </>
          )}
          {isTimed && <span className="w-24 text-center">TIME (MM:SS)</span>}
          {isDistance && (
            <>
              <span className="w-24 text-center">
                DISTANCE ({distanceDisplay.label})
              </span>
              <span className="w-24 text-center">TIME (MM:SS)</span>
            </>
          )}
        </div>
        <span className="w-8 text-center" />
      </div>

      {sets.map((set, index) => {
        const distanceValue =
          set.targetDistanceMeters !== undefined && set.targetDistanceMeters !== null
            ? Math.round(
                metersToDistanceDisplay(
                  set.targetDistanceMeters,
                  trackingType,
                  distanceUnit,
                ) * 100,
              ) / 100
            : "";

        return (
          <div
            key={set.id}
            className={cn(
              "group/period-set-row my-1 flex flex-wrap items-center gap-3 rounded-md px-1.5 py-1 lg:flex-nowrap",
            )}
          >
            <span
              className="flex size-7 items-center justify-center rounded-md border-1 bg-background p-1 text-center text-sm font-medium"
            >
              {index + 1}
            </span>
            <div className="flex flex-grow flex-wrap items-center justify-center gap-2 lg:flex-nowrap">
              {!isTimed && !isDistance && (
                <>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={set.startRange ?? ""}
                    onChange={(event) => {
                      const { value, valueAsNumber } = event.target;
                      if (value !== "" && Number.isNaN(valueAsNumber)) return;
                      updateSet(set.id, {
                        startRange: value === "" ? undefined : valueAsNumber,
                      });
                    }}
                    className="h-8 w-11 text-center text-sm"
                  />
                  <span className="text-center text-sm">-</span>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={set.endRange ?? ""}
                    onChange={(event) => {
                      const { value, valueAsNumber } = event.target;
                      if (value !== "" && Number.isNaN(valueAsNumber)) return;
                      updateSet(set.id, {
                        endRange: value === "" ? undefined : valueAsNumber,
                      });
                    }}
                    className="h-8 w-11 text-center text-sm"
                  />
                  {trackingType === "strength" && (
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      value={set.targetWeight ?? ""}
                      onChange={(event) => {
                        const { value, valueAsNumber } = event.target;
                        if (value === "") {
                          updateSet(set.id, { targetWeight: undefined });
                          return;
                        }
                        if (Number.isNaN(valueAsNumber)) return;
                        updateSet(set.id, { targetWeight: valueAsNumber });
                      }}
                      className="h-8 w-24 text-center text-sm"
                    />
                  )}
                </>
              )}

              {isTimed && (
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formatTimeInput(set.targetTimeSeconds)}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (value === "") {
                      updateSet(set.id, { targetTimeSeconds: undefined });
                      return;
                    }
                    const parsed = parseTimeDigitsToSeconds(value);
                    if (parsed === null) {
                      updateSet(set.id, { targetTimeSeconds: undefined });
                      return;
                    }
                    updateSet(set.id, {
                      targetTimeSeconds: parsed === 0 ? undefined : parsed,
                    });
                  }}
                  className="h-8 w-24 text-center text-sm"
                  placeholder="00:00"
                />
              )}

              {isDistance && (
                <>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={distanceValue}
                    onChange={(event) => {
                      const { value, valueAsNumber } = event.target;
                      if (value === "") {
                        updateSet(set.id, { targetDistanceMeters: undefined });
                        return;
                      }
                      if (Number.isNaN(valueAsNumber)) return;
                      updateSet(set.id, {
                        targetDistanceMeters: distanceDisplayToMeters(
                          valueAsNumber,
                          trackingType,
                          distanceUnit,
                        ),
                      });
                    }}
                    className="h-8 w-24 text-center text-sm"
                  />
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={formatTimeInput(set.targetTimeSeconds)}
                    onChange={(event) => {
                      const { value } = event.target;
                      if (value === "") {
                        updateSet(set.id, { targetTimeSeconds: undefined });
                        return;
                      }
                      const parsed = parseTimeDigitsToSeconds(value);
                      if (parsed === null) {
                        updateSet(set.id, { targetTimeSeconds: undefined });
                        return;
                      }
                      updateSet(set.id, {
                        targetTimeSeconds: parsed === 0 ? undefined : parsed,
                      });
                    }}
                    className="h-8 w-24 text-center text-sm"
                    placeholder="00:00"
                  />
                </>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 w-8 p-0 opacity-0 transition-opacity group-hover/period-set-row:opacity-100 group-focus-within/period-set-row:opacity-100 [@media(pointer:coarse)]:opacity-100",
                sets.length === 1 && "!opacity-0",
              )}
              onClick={() => removeSet(set.id)}
              disabled={sets.length === 1}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      })}

      {sets.length === 0 ? (
        <div className="my-1 flex items-center gap-3 rounded-md px-1.5 py-1">
          <span className="flex-grow text-center text-sm">No sets</span>
        </div>
      ) : null}

      <Button
        variant="outline"
        onClick={addSet}
        className="mt-2 h-8 w-full justify-center p-0 opacity-0 transition-opacity group-hover/period-cell:opacity-100 group-focus-within/period-cell:opacity-100 [@media(pointer:coarse)]:opacity-100"
      >
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        Add Set
      </Button>
    </div>
  );
};
